import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, localizedField } from "@/lib/i18n";
import { ATLAS } from "@/constants/testIds";
import { ArrowLeft, Code2, Sparkles, FileDown, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import MapEmbed from "@/components/MapEmbed";
import AtlasBlocksSection from "@/components/AtlasBlocksSection";
import BackButton from "@/components/BackButton";

export default function AppellationDetail() {
  const { slug } = useParams();
  const { user } = useAuth();  // eslint-disable-line no-unused-vars
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    api.get(`/appellations/${slug}`).then((r) => setItem(r.data)).catch(() => setItem(null));
  }, [slug]);

  const exportPdf = async () => {
    if (!printRef.current || !item) return;
    setPdfBusy(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const node = printRef.current;
      node.classList.add("printing");
      await html2pdf()
        .from(node)
        .set({
          margin: [10, 10],
          filename: `geovinum-appellation-${(item.slug || slug).toLowerCase()}.pdf`,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .save();
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Export PDF impossible");
    } finally {
      printRef.current?.classList.remove("printing");
      setPdfBusy(false);
    }
  };

  if (!item) return <div className="max-w-4xl mx-auto px-6 py-24 text-[#8B95A1]">{t("common.loading")}</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-16">
      <Toaster theme="light" />
      <div className="hide-in-pdf">
        <BackButton fallback="/appellations" />
      </div>

      <div ref={printRef}>
        <div className="mt-6">
          <span className="chip-pink">{item.type} · {item.region}, {item.country}</span>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight text-[#1F3A5F] mt-4 font-medium">{item.name}</h1>
          <p className="mt-5 text-base md:text-lg text-[#4A5568] max-w-3xl font-light leading-relaxed">{localizedField(item, "description", lang)}</p>
        </div>

        <div className="mt-10">
          <div className="hide-in-pdf">
            <MapEmbed appellation={item.name} height={540} />
          </div>
          <div className="mt-4 flex flex-wrap gap-3 hide-in-pdf">
            <button
              onClick={() => navigate(`/chat?q=${encodeURIComponent(`Parle-moi de ${item.name}`)}`)}
              className="btn-primary"
            >
              <Sparkles className="w-4 h-4" /> {t("cepages.detail.askAi")}
            </button>
            <Link to={`/pro?app=${encodeURIComponent(item.name)}`} data-testid={ATLAS.detailEmbedBtn} className="btn-secondary">
              <Code2 className="w-4 h-4" /> {t("app.detail.embed")}
            </Link>
            <button
              onClick={exportPdf}
              disabled={pdfBusy}
              data-testid="appellation-pdf-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A] disabled:opacity-50"
            >
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Exporter en PDF
            </button>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-10">
          <div>
            <SectionTitle>{t("app.detail.regulation")}</SectionTitle>
            <p className="text-base text-[#1F3A5F] leading-relaxed font-light">{localizedField(item, "regulation", lang)}</p>
          </div>
          <div>
            <SectionTitle>{t("app.detail.terroir")}</SectionTitle>
            <p className="text-base text-[#1F3A5F] leading-relaxed font-light">{localizedField(item, "terroir", lang)}</p>
          </div>
          <div>
            <SectionTitle>{t("app.detail.grapes")}</SectionTitle>
            <div className="flex flex-wrap gap-2 mt-2">
              {(item.authorized_grapes || []).map((g) => (
                <span key={g} className="px-3 py-1.5 rounded-full border border-[#1F3A5F]/12 text-sm text-[#1F3A5F] bg-white">{g}</span>
              ))}
            </div>
          </div>
          <div>
            <SectionTitle>{t("app.detail.producers")}</SectionTitle>
            <ul className="space-y-1.5 mt-2">
              {(item.producers || []).map((p) => (
                <li key={p} className="text-base text-[#1F3A5F] font-light">— {p}</li>
              ))}
            </ul>
          </div>
        </div>

        <AtlasBlocksSection entityType="appellation" slug={item.slug || slug} />
      </div>

      <style>{`.printing .hide-in-pdf { display: none !important; }`}</style>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-semibold mb-3">{children}</div>
);
